import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { QrCode, Image as ImageIcon, Download, Upload, Trash2, Printer, ImagePlus, Edit, RefreshCw } from "lucide-react";
import PrefixImageEditor from "../components/PrefixImageEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const API = `${BACKEND_URL}/api`;

export default function SharedTemplate() {
  const { templateId } = useParams();
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQR, setShowQR] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [showImages, setShowImages] = useState(false);
  const [templateImages, setTemplateImages] = useState([]);
  const [showPrefixEditor, setShowPrefixEditor] = useState(false);
  const [ownerImageFile, setOwnerImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTemplate();
    fetchImages();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}`);
      setTemplate(response.data);
      setLoading(false);
    } catch (error) {
      setError("Template not found or access denied");
      setLoading(false);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/templates/${templateId}/images`);
      setTemplateImages(response.data);
    } catch (error) {
      console.error("Failed to load images");
    }
  };

  const viewQRCode = () => {
    setQrCodeUrl(`${API}/templates/${templateId}/qr`);
    setShowQR(true);
  };

  const downloadPDF = () => {
    window.open(`${API}/templates/${templateId}/pdf`, '_blank');
  };

  const copyShareLink = () => {
    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    const shareUrl = `${frontendUrl}/shared/${templateId}`;
    navigator.clipboard.writeText(shareUrl);
    alert("Share link copied to clipboard!\n\n" + shareUrl);
  };

  const handlePrefixSave = async (formData) => {
    await axios.post(`${API}/templates/${templateId}/prefix-image`, formData);
    fetchTemplate();
  };

  const uploadOwnerImage = async () => {
    if (!ownerImageFile) {
      alert("Please select an image");
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", ownerImageFile);
      formData.append("user_name", "Admin Upload");
      formData.append("rotation", "0");
      
      await axios.post(`${API}/upload/${templateId}`, formData);
      alert("Image uploaded successfully!");
      setOwnerImageFile(null);
      fetchTemplate();
      fetchImages();
    } catch (error) {
      alert("Failed to upload image");
    }
    setUploadingImage(false);
  };

  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const bulkFileInputRef = useRef(null);
  const [oneToAllFile, setOneToAllFile] = useState(null);
  const [applyingOneToAll, setApplyingOneToAll] = useState(false);

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setBulkUploading(true);
    setBulkProgress({ current: 0, total: files.length });

    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBulkProgress({ current: i + 1, total: files.length });

      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_name", `Bulk Upload ${i + 1}`);
        formData.append("rotation", "0");

        await axios.post(`${API}/upload/${templateId}`, formData);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    setBulkUploading(false);
    setBulkProgress({ current: 0, total: 0 });
    
    // Clear the input
    if (bulkFileInputRef.current) {
      bulkFileInputRef.current.value = '';
    }

    alert(`Bulk upload complete!\n✅ ${successCount} uploaded\n❌ ${failCount} failed`);
    fetchTemplate();
    fetchImages();
  };

  // One photo to all empty slots
  const applyOneToAllSlots = async () => {
    if (!oneToAllFile) {
      alert("Please select an image first");
      return;
    }

    if (!template) return;

    const emptySlots = template.total_slots - template.filled_count;
    if (emptySlots <= 0) {
      alert("All slots are already filled. Delete some images first.");
      return;
    }

    if (!window.confirm(`Apply this image to ${emptySlots} empty slots?`)) {
      return;
    }

    setApplyingOneToAll(true);

    try {
      const formData = new FormData();
      formData.append("file", oneToAllFile);
      
      const response = await axios.post(`${API}/templates/${templateId}/bulk-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      
      alert(`✅ ${response.data.filled_slots} slots filled with the same image!`);
      
      setOneToAllFile(null);
      fetchTemplate();
      fetchImages();
    } catch (error) {
      console.error("One-to-all upload error:", error);
      alert(error.response?.data?.detail || "Failed to apply to slots");
    } finally {
      setApplyingOneToAll(false);
    }
  };

  const deleteImage = async (imageId) => {
    if (!window.confirm("Delete this image?")) return;
    
    try {
      await axios.delete(`${API}/images/${imageId}`);
      fetchImages();
      fetchTemplate();
    } catch (error) {
      alert("Failed to delete image");
    }
  };

  const printTemplate = () => {
    window.open(`${API}/templates/${templateId}/pdf`, '_blank');
  };

  const openEditor = () => {
    // Use the shared editor, not the admin editor
    window.location.href = `/shared/${templateId}/editor`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="text-center py-12">
            <p className="text-red-500 text-lg">{error}</p>
            <p className="text-gray-500 mt-2">Please check the link or contact the admin.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            {template.template_type === "branded" ? "🏷️" : template.template_type === "single" ? "📄" : template.template_type === "event" ? "🎉" : "⭕"} {template.name}
          </h1>
          <p className="text-gray-600 mt-2">
            {template.template_type === "branded" 
              ? `Branded Card 2.25"×3.25"`
              : template.template_type === "single"
              ? `Single Photo Card`
              : template.template_type === "event"
              ? `Event Gallery`
              : `Photo Template`
            } • {template.filled_count} photos uploaded
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => { fetchTemplate(); fetchImages(); }}
            className="mt-2"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Actions */}
          <div className="lg:col-span-2 space-y-6">
            {/* Progress Card */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <CardTitle>Photos Collected</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Total Photos</span>
                    <span className="text-3xl font-bold text-purple-600">
                      {template.filled_count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full transition-all"
                      style={{ width: `${Math.min((template.filled_count / Math.max(template.total_slots, 1)) * 100, 100)}%` }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* QR Code & Share */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <CardTitle>Share with Guests</CardTitle>
                <CardDescription>Display the QR code for guests to upload photos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    onClick={viewQRCode}
                    className="h-20 text-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                  >
                    <div className="flex flex-col items-center">
                      <QrCode className="w-6 h-6 mb-1" />
                      View QR Code
                    </div>
                  </Button>
                  
                  <Button 
                    onClick={copyShareLink}
                    variant="outline"
                    className="h-20 text-lg"
                  >
                    <div className="flex flex-col items-center">
                      <Upload className="w-6 h-6 mb-1" />
                      Copy Share Link
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Images Grid */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Uploaded Photos ({templateImages.length})</CardTitle>
                    <CardDescription>Click to download, hover to delete</CardDescription>
                  </div>
                  <Button onClick={() => { fetchImages(); fetchTemplate(); }} variant="outline" size="sm">
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {templateImages.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No photos uploaded yet</p>
                    <p className="text-sm">Share the QR code to collect photos!</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {templateImages.map((img) => (
                      <div key={img.id} className="relative group">
                        <div className="aspect-[2.25/3.25] rounded-lg overflow-hidden border-2 border-gray-200 group-hover:border-purple-500 transition-all">
                          <img
                            src={`${API}/images/${img.id}`}
                            alt={`Photo ${img.circle_index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        {img.user_name && (
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white px-1 py-0.5 text-xs truncate">
                            {img.user_name}
                            {img.user_contact && <span className="ml-1 opacity-75">• {img.user_contact}</span>}
                          </div>
                        )}
                        <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <a
                            href={`${API}/images/${img.id}/download`}
                            download
                            className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-full"
                          >
                            <Download className="w-3 h-3" />
                          </a>
                          <button
                            onClick={() => deleteImage(img.id)}
                            className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Tools */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  onClick={openEditor}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Open Full Editor
                </Button>
                
                <Button 
                  onClick={downloadPDF}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                
                <Button 
                  onClick={printTemplate}
                  variant="outline"
                  className="w-full"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
              </CardContent>
            </Card>

            {/* Bulk Upload */}
            <Card className="border-0 shadow-xl bg-white/90 border-2 border-dashed border-purple-300">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Upload className="w-5 h-5 text-purple-600" />
                  Bulk Upload Options
                </CardTitle>
                <CardDescription>Multiple ways to fill your template</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Option 1: Multiple images to multiple slots */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="text-sm font-semibold text-purple-800 mb-2">📤 Upload Multiple Images</h3>
                  <p className="text-xs text-purple-600 mb-2">Select many images - each fills one empty slot</p>
                  <input
                    ref={bulkFileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleBulkUpload}
                    className="hidden"
                  />
                  <Button
                    onClick={() => bulkFileInputRef.current?.click()}
                    disabled={bulkUploading}
                    className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    {bulkUploading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        Uploading {bulkProgress.current}/{bulkProgress.total}...
                      </div>
                    ) : (
                      <>
                        <Upload className="w-5 h-5 mr-2" />
                        Select Multiple Images
                      </>
                    )}
                  </Button>
                  {bulkUploading && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all"
                        style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                      ></div>
                    </div>
                  )}
                </div>

                {/* Option 2: One image to all slots */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-sm font-semibold text-green-800 mb-2">🔄 One Photo to All Slots</h3>
                  <p className="text-xs text-green-600 mb-2">Same image fills ALL empty slots</p>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setOneToAllFile(e.target.files?.[0] || null)}
                    className="mb-2"
                  />
                  {oneToAllFile && (
                    <div className="text-sm text-gray-600 mb-2">
                      Selected: {oneToAllFile.name}
                    </div>
                  )}
                  <Button
                    onClick={applyOneToAllSlots}
                    disabled={!oneToAllFile || applyingOneToAll}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600"
                  >
                    {applyingOneToAll ? "Applying..." : "Apply to All Empty Slots"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Single Upload */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <CardTitle>Add Single Image</CardTitle>
                <CardDescription>Upload one photo at a time</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setOwnerImageFile(e.target.files[0])}
                />
                <Button
                  onClick={uploadOwnerImage}
                  disabled={!ownerImageFile || uploadingImage}
                  className="w-full"
                >
                  <ImagePlus className="w-4 h-4 mr-2" />
                  {uploadingImage ? "Uploading..." : "Upload Image"}
                </Button>
              </CardContent>
            </Card>

            {/* Prefix Image (for branded templates) */}
            {template.template_type === "branded" && (
              <Card className="border-0 shadow-xl bg-white/90">
                <CardHeader>
                  <CardTitle>Prefix/Logo Image</CardTitle>
                  <CardDescription>Bottom 1/4 branding image</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {template.prefix_image_url && (
                    <div className="mb-3">
                      <img 
                        src={`${API}/prefix-images/${templateId}`}
                        alt="Current prefix"
                        className="w-full h-24 object-cover rounded-lg border"
                      />
                      <p className="text-xs text-gray-500 mt-1">Current prefix image</p>
                    </div>
                  )}
                  <Button
                    onClick={() => setShowPrefixEditor(true)}
                    className="w-full bg-purple-500 hover:bg-purple-600"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {template.prefix_image_url ? "Update Prefix Image" : "Upload Prefix Image"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Template Info */}
            <Card className="border-0 shadow-xl bg-white/90">
              <CardHeader>
                <CardTitle>Template Info</CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500">Type:</span>
                  <span className="font-medium">{template.template_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Photos:</span>
                  <span className="font-medium">{template.filled_count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Capacity:</span>
                  <span className="font-medium">{template.total_slots}</span>
                </div>
                {template.auto_print && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Auto Print:</span>
                    <span className="font-medium text-green-600">ON</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* QR Code Dialog */}
        <Dialog open={showQR} onOpenChange={setShowQR}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>QR Code for Photo Upload</DialogTitle>
              <DialogDescription>
                Display this at your event for guests to scan
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col items-center gap-4">
              <img 
                src={qrCodeUrl} 
                alt="QR Code" 
                className="w-80 h-80 border rounded-lg"
              />
              <p className="text-sm text-gray-500 text-center">
                Guests scan this code to upload their photos
              </p>
              <div className="flex gap-2 w-full">
                <Button 
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = qrCodeUrl;
                    link.download = `${template.name}-qr.png`;
                    link.click();
                  }}
                  className="flex-1"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download QR
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    const frontendUrl = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
                    const uploadUrl = `${frontendUrl}/upload/${templateId}`;
                    navigator.clipboard.writeText(uploadUrl);
                    alert("Upload link copied!\n\n" + uploadUrl);
                  }}
                  className="flex-1"
                >
                  Copy Upload Link
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Prefix Image Editor */}
        <PrefixImageEditor
          isOpen={showPrefixEditor}
          onClose={() => setShowPrefixEditor(false)}
          onSave={handlePrefixSave}
          templateId={templateId}
        />
      </div>
    </div>
  );
}
